import { GridColumnVisibilityModel, GridRowModel } from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import useFilters from 'ext-ui/hooks/filters/filters'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DownloadTransactionsModal from 'src/component-modal/DownloadTransactionsModal/DownloadTransactionsModal'
import ManagerReviewRejectionModal from 'src/component-modal/ManagerReviewRejectionModal/ManagerReviewRejectionModal'
import Box from 'src/components/Box/Box'
import FullTransactionsTable from 'src/components/FullTransactionsTable/FullTransactionsTable'
import { ApprovalActions } from 'src/components/ReportTransactionsBoxAndLinks/ApprovalActions'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SelectAllTransactionsBox from 'src/components/SelectAllTransactionsBox/SelectAllTransactionsBox'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import {
  makeBaseTransactionsSchema,
  TransactionsFilters,
  TransactionsFiltersBody,
} from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import TransactionsListFilter from 'src/components/TransactionsListFilter/TransactionsListFilter'
import { CollapseWithFrozenExit } from 'src/components/TransitionWithFrozenExit/TransitionWithFrozenExit'
import { TransactionFilterOptions } from 'src/enums'
import AccountingSummaryBar from 'src/feature/AccountingSummaryBar/AccountingSummaryBar'
import {
  TransactionDateType,
  TransactionManagerReviewStatus,
  TransactionSort,
} from 'src/models/transaction.models'
import { usePatchTransactionManagerReview } from 'src/queries/expenseReview.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryTransactions } from 'src/queries/transactions.queries'
import { updateSelectAllTransactionsBoxVisible } from 'src/redux/actions/data/transactions.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUpdatedReceiptRulesFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectExpenseReportIdFromRoute } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectAccountingStatsHeaderWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectSelectAllTransactionsBoxVisibleFromState,
  selectSelectedTransactionIdsFromState,
} from 'src/redux/selectors/data/transactions.selectors'
import { InfiniteScrollProps } from 'src/types'

import styles from './ReportTransactionsBoxAndLinks.style'

export interface TransactionsAnalyticsEventProperties {
  accounts?: string[]
  card_user?: string[]
  clear_date?: boolean
  transaction_date?: boolean
  departments?: string[]
  merchant_category?: string[]
  status?: string[]
  transaction_origin?: string[]
  date_picker?: string
  search?: string
  receipt_status?: string[]
  receipts_required?: boolean
  expense_category_statuses?: string[]
  expense_category_labels?: string[]
  sync_statuses?: boolean
  review_status?: boolean
  amount_range?: boolean
}

interface ReportTransactionsBoxAndLinksProps {
  className?: string
  context: TransactionReportType
  showIntegrationsLink?: boolean
  design: TransactionsTableLargeDesigns
  additionalItems?: React.ReactElement[]
  hasReceiptToolsDropdown?: boolean
  showConnection?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
  canViewAllTransactions?: boolean
  reportId?: string
  visibleFilters: TransactionFilterOptions[]
  clientHeight: number
  creditCardId?: string
  externallyAppliedFilters?: TransactionsFilters
  /* Initial filters to apply on first load, on top of defaults */
  initialFilters?: Partial<TransactionsFilters>
}

const ReportTransactionsBoxAndLinks: React.FC<
  ReportTransactionsBoxAndLinksProps
> = (props): React.ReactElement => {
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const queryClient = useQueryClient()
  const updatedReceiptRules = useSelector(selectUpdatedReceiptRulesFromState)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const {
    show_manager_review_rejection_reason: showManagerReviewRejectionReason,
  } = useSelector(selectFeatureFlaggerFromState)

  const showAutoExpenseReportOverhaul = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_auto_expense_report_overhaul

  const reportId = useSelector(selectExpenseReportIdFromRoute)
  const user = useSelector(selectUserListModelFromState)
  const { mutate: patchTransactionManagerReview } =
    usePatchTransactionManagerReview()

  const accountingStatsHeaderWhitelist = useSelector(
    selectAccountingStatsHeaderWhitelistFromRemoteConfigState,
  )

  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false)
  const [showManagerReviewRejectionModal, setShowManagerReviewRejectionModal] =
    useState<boolean>(false)

  // pick a safe initial value (auth)
  const [sort, setSort] = useState<TransactionSort>(
    TransactionSort.dateAuthDesc,
  )

  // expense categories schema needed to pull out labels for api request
  const { data: expenseCategories } = useQueryExpenseCategories({
    filters: { active: true },
  })

  const filterOptions = useMemo(
    () =>
      makeBaseTransactionsSchema({
        activeConnectedPlatform: activeIntegration?.connectedPlatform,
        reportId,
        context: props.context,
        userId: user?.id,
        creditCardId: props.creditCardId,
        expenseCategories,
        sort,
      }),
    [
      activeIntegration?.connectedPlatform,
      reportId,
      props.context,
      props.creditCardId,
      expenseCategories,
      sort,
    ],
  )

  const {
    filters: currFilters,
    apiQueryParams,
    handleFilterChange,
    resetFilters,
    resetSingleFilter,
    selectedFiltersCount,
  } = useFilters<TransactionsFilters, TransactionsFiltersBody>({
    ...filterOptions,
    initialFilters: props.initialFilters,
  })

  // One-time snapshot of the very first filters (initial URL + defaults)
  const initialFilters = React.useRef(currFilters).current

  // once filters resolve (from URL or defaults), sync sort to dateType
  useEffect(() => {
    const dt = currFilters.dateType ?? TransactionDateType.Transaction
    setSort(
      dt === TransactionDateType.Clear
        ? TransactionSort.dateClearDesc
        : TransactionSort.dateAuthDesc,
    )
  }, [currFilters.dateType])

  useEffect(() => {
    // update filters when externallyAppliedFilters change
    // this allows other components to update the filters
    // ex OrgExpenseReportSummaryBar
    handleFilterChange({
      ...currFilters,
      ...props.externallyAppliedFilters,
    })
  }, [props.externallyAppliedFilters])

  // fire analytics when filters are applied (use debounced value)
  useEffect(() => {
    const filterApplied = Object.keys(apiQueryParams).length > 1
    if (filterApplied) {
      analytics?.track(
        props.context.toLowerCase() + ' transactions filter applied',
        {
          search: !!apiQueryParams.search,
          date_picker: apiQueryParams.dateType,
          clear_date: apiQueryParams.dateType === TransactionDateType.Clear,
          transaction_date:
            apiQueryParams.dateType === TransactionDateType.Transaction,
          amount_range:
            apiQueryParams?.maxClearingBillingCents !== undefined ||
            apiQueryParams?.minClearingBillingCents !== undefined,
          status: !!apiQueryParams.status?.length,
          transaction_origin: !!apiQueryParams.source?.length,
          expense_category_status:
            !!apiQueryParams.expenseCategoryStatuses?.length,
          expense_category_labels:
            !!apiQueryParams.expenseCategoryLabel?.length,
          receipt_status: !!apiQueryParams.receiptStatus?.length,
          receipts_required: !!apiQueryParams.receiptRequired,
          sync_status: !!apiQueryParams.syncStatuses?.length,
          card_user: !!apiQueryParams.recipientOrPhysicalCardUserId?.length,
          departments: !!apiQueryParams.departments?.length,
          accounts: !!apiQueryParams.creditCardId?.length,
          merchant_category: !!apiQueryParams.mccGroup?.length,
          review_status: !!apiQueryParams.reviewStatus?.length,
        },
      )
    }
  }, [props.context, queryString.stringify(apiQueryParams)])

  const { data, fetchNextPage, isFetching, hasNextPage, complete } =
    useQueryTransactions({
      filters: apiQueryParams,
      myTransactions: props.context === TransactionReportType.MyTransactions,
    })

  const selectAllTransactionsBoxVisible = useSelector(
    selectSelectAllTransactionsBoxVisibleFromState,
  )

  const transactionInfiniteScrollProps: InfiniteScrollProps<TransactionsFiltersBody> =
    {
      fetch: () => fetchNextPage(),
      loading: isFetching,
      complete,
      hasNextPage,
    }

  // Reset transactions table and filter when receipt rules is updated
  useEffect((): void => {
    if (updatedReceiptRules) {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
    }
  }, [updatedReceiptRules])

  let defaultColumnVisibilityModel: GridColumnVisibilityModel = {
    date: true,
    merchant: true,
    vendor: activeIntegration !== undefined || false,
    card: true,
    cardUser: true,
    cardholderEmail: false,
    expenseCategories: true,
    status: false,
    amount: true,
    currency: false,
    authBillingAmountCents: false,
    authBillingCurrency: false,
    authMerchantAmountCents: false,
    authMerchantCurrency: false,
    authExchangeRate: false,
    attachmentsCount: false,
    creditCardDisplayName: false,
    creditCardType: false,
    receipts: true,
    integration: activeIntegration !== undefined || false,
    originalMerchant: false,
    managerReviewStatus: false,
    finalReview: false,
  }

  switch (props.context) {
    case TransactionReportType.MyTransactions:
      defaultColumnVisibilityModel.cardUser = false
      break
    case TransactionReportType.NeedsApproval:
      defaultColumnVisibilityModel.status = true
      break
    case TransactionReportType.AccountingTransactions:
      defaultColumnVisibilityModel.managerReviewStatus = true
      defaultColumnVisibilityModel.finalReview = true
      break
  }

  // select all box cleanup
  useEffect(() => {
    dispatch(
      updateSelectAllTransactionsBoxVisible(
        selectedTransactionIds.size === data?.transactions?.length &&
          selectedTransactionIds.size > 0 &&
          !complete,
      ),
    )
  }, [selectedTransactionIds, data?.transactions, complete])

  const [rejectedTxnIds, setRejectedTxnIds] = useState<string[]>([])
  const onReview = useCallback(
    (txId: string, status: TransactionManagerReviewStatus) => {
      if (
        status === TransactionManagerReviewStatus.Rejected &&
        showManagerReviewRejectionReason
      ) {
        setShowManagerReviewRejectionModal(true)
        setRejectedTxnIds([txId])
      } else {
        patchTransactionManagerReview({
          body: {
            transactionIds: [txId],
            managerReviewStatus: status,
          },
          filters: apiQueryParams,
        })
        analytics?.track('manager review actioned txn', {
          action:
            status === TransactionManagerReviewStatus.Rejected
              ? 'rejected'
              : 'approved',
        })
      }
    },
    [
      patchTransactionManagerReview,
      apiQueryParams,
      analytics,
      showManagerReviewRejectionReason,
    ],
  )

  const rowApprovalActions = useCallback(
    (params: GridRowModel) => {
      return (
        <ApprovalActions txId={params.row.transaction.id} onReview={onReview} />
      )
    },
    [onReview],
  )

  const showAccountingStatsHeader =
    showAutoExpenseReportOverhaul ||
    accountingStatsHeaderWhitelist?.includes(user?.organizationId || '')

  return (
    <>
      {props.context === TransactionReportType.AccountingTransactions &&
        showAccountingStatsHeader && (
          // this is defined here to give it access to the filters
          <AccountingSummaryBar
            filters={currFilters}
            handleFilterChange={handleFilterChange}
            resetSingleFilter={resetSingleFilter}
            apiFiltersBody={apiQueryParams}
          />
        )}
      <Box
        className={cn('ReportTransactionsBoxAndLinks', props.className)}
        css={styles}
        noPadding
        noShadow
        outlineBorder
      >
        <CollapseWithFrozenExit
          in={
            props.context !== TransactionReportType.NeedsApproval &&
            selectAllTransactionsBoxVisible
          }
          key={'showSelectAllBox'}
          mountOnEnter
          unmountOnExit
        >
          <SelectAllTransactionsBox
            filtersBody={apiQueryParams}
            selectedTransactionCount={selectedTransactionIds.size}
          />
        </CollapseWithFrozenExit>
        <TransactionsListFilter
          initialFilterValues={initialFilters}
          defaultFilters={filterOptions.defaultFilters}
          filterValues={currFilters}
          selectedFiltersCount={selectedFiltersCount}
          handleFilterChange={handleFilterChange}
          resetFilters={resetFilters}
          resetSingleFilter={resetSingleFilter}
          apiQueryParams={apiQueryParams}
          reportId={props.reportId}
          hasReceiptToolsDropdown={props.hasReceiptToolsDropdown}
          context={props.context}
          visibleFilters={props.visibleFilters}
          isTransactionsFetching={isFetching}
          handleDownloadClick={() => setShowDownloadModal(true)}
        />
        <div className="ReportTransactionsBoxAndLinks table">
          <FullTransactionsTable
            transactions={data?.transactions ?? []}
            fullyLoadedTransactions={complete}
            emptyStateDesign={
              props.context === TransactionReportType.NeedsApproval
                ? TableEmptyStateDesigns.ExpensesNeedsApproval
                : TableEmptyStateDesigns.AccountTransactions
            }
            design={props.design}
            infiniteScrollProps={transactionInfiniteScrollProps}
            filters={apiQueryParams}
            reportId={props.reportId}
            clientHeight={props.clientHeight}
            setSort={setSort}
            defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            context={props.context}
            rowApprovalActions={
              props.context === TransactionReportType.NeedsApproval
                ? rowApprovalActions
                : undefined
            }
          />
        </div>
        <DownloadTransactionsModal
          isOpen={showDownloadModal}
          onClose={() => {
            setShowDownloadModal(false)
          }}
          apiQueryParams={apiQueryParams}
        />
        <ManagerReviewRejectionModal
          isOpen={showManagerReviewRejectionModal}
          onClose={() => {
            setShowManagerReviewRejectionModal(false)
          }}
          transactionIds={rejectedTxnIds}
        />
      </Box>
    </>
  )
}

export default ReportTransactionsBoxAndLinks
